import { useCallback, useEffect, useMemo, useState } from 'react'
import { acknowledgeAnnouncement, loadMyAnnouncements, markAnnouncementSeen, type Announcement } from '../lib/announcements'
import { useAuth } from '../auth/AuthProvider'
import { supabase } from '../lib/supabase'

function priorityLabel(priority: Announcement['priority']) {
  if (priority === 'critico') return 'Crítico'
  if (priority === 'alerta') return 'Alerta'
  return 'Informação'
}

function formatDate(value: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR')
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M10 21h4" />
    </svg>
  )
}

export function AnnouncementBell() {
  const { profile, offlineProfile } = useAuth()
  const [items, setItems] = useState<Announcement[]>([])
  const [open, setOpen] = useState(false)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [error, setError] = useState('')

  const refresh = useCallback(async () => {
    if (!profile?.ativo || offlineProfile || !navigator.onLine) return
    try {
      setItems(await loadMyAnnouncements())
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Comunicados indisponíveis.')
    }
  }, [profile?.ativo, offlineProfile])

  useEffect(() => {
    void refresh()
    const timer = window.setInterval(() => void refresh(), 60_000)
    const onFocus = () => void refresh()
    window.addEventListener('focus', onFocus)

    const channel = supabase
      .channel(`suite-announcements-${profile?.id ?? 'anonymous'}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'announcements' }, () => void refresh())
      .subscribe()

    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      void supabase.removeChannel(channel)
    }
  }, [refresh, profile?.id])

  const pending = useMemo(() => items.filter((item) => item.unread || item.pending_ack).length, [items])

  async function toggleRead(item: Announcement) {
    const expanding = expandedId !== item.id
    setExpandedId(expanding ? item.id : null)
    if (expanding && item.unread) {
      try {
        await markAnnouncementSeen(item.id)
        await refresh()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Não foi possível registrar a leitura.')
      }
    }
  }

  async function acknowledge(id: number) {
    try {
      await acknowledgeAnnouncement(id)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível registrar ciência.')
    }
  }

  return (
    <div className="announcement-bell-wrap">
      <button type="button" className={`announcement-bell ${pending ? 'has-pending' : ''}`} onClick={() => setOpen((value) => !value)} aria-label={`Comunicados${pending ? `, ${pending} pendentes` : ''}`} aria-expanded={open}>
        <BellIcon />
        {pending > 0 && <b>{pending > 99 ? '99+' : pending}</b>}
      </button>

      {open && (
        <section className="announcement-popover" aria-label="Centro de comunicados">
          <header>
            <div><p className="eyebrow">COMUNICADOS</p><h3>Central de avisos</h3></div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Fechar">×</button>
          </header>

          {error && <div className="notice error">{error}</div>}
          {!error && items.length === 0 && <div className="empty-state">Nenhum comunicado ativo para você.</div>}

          <div className="announcement-list">
            {items.map((item) => {
              const expanded = expandedId === item.id
              return (
                <article key={item.id} className={`announcement-card ${item.priority} ${item.pending_ack ? 'pending' : ''} ${item.unread ? 'unread' : ''}`}>
                  <div className="announcement-card-meta">
                    <span>{priorityLabel(item.priority)}</span>
                    {item.reference_code && <b>{item.reference_code}</b>}
                    {item.unread && <b>Novo</b>}
                    <time>{formatDate(item.starts_at)}</time>
                  </div>
                  <h4>{item.title}</h4>
                  {expanded && <p>{item.body}</p>}
                  <div className="announcement-card-footer">
                    <button type="button" className="secondary-button compact" onClick={() => void toggleRead(item)}>{expanded ? 'Recolher' : 'Ler comunicado'}</button>
                    {expanded && item.require_ack && !item.acknowledged_at && <button type="button" className="primary-button compact" onClick={() => void acknowledge(item.id)}>✓ Estou ciente</button>}
                    {expanded && item.acknowledged_at && <span className="announcement-ack-done">✓ Ciente em {formatDate(item.acknowledged_at)}</span>}
                    {expanded && !item.require_ack && <span className="announcement-ack-done">Comunicado informativo</span>}
                  </div>
                </article>
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}
